import {Link, TargetBlank} from '@wandb/weave/common/util/links';
import React from 'react';
import {Maybe} from 'src/generated/graphql';

import {ErrorBox} from '../../../../../../components/ErrorBox';
import {envIsPublicCloud} from '../../../../../../config';
import history from '../../../../../../util/history';
import {
  accountSettingsBillingTab,
  accountSettingsMembersTab,
  accountSettingsUsagePage,
  contactSalesPricing,
  deployer,
  storageFilePath,
} from '../../../../../../util/urls';
import * as S from '../../CurrentPlanSection.styles';
import {PlanCardProps} from './PlanCard';
import {PlanCardWrapper} from './PlanCard.styles';

type ButtonProps = {
  organizationName: string;
};

type EntityButtonProps = {
  entityName: string;
};

export const ManageUserButton = ({organizationName}: ButtonProps) => {
  return (
    <Link to={accountSettingsMembersTab(organizationName)}>
      <S.SecondaryCTAButton>Manage users</S.SecondaryCTAButton>
    </Link>
  );
};

export const PrimaryManageUserButton = ({organizationName}: ButtonProps) => {
  return (
    <Link to={accountSettingsMembersTab(organizationName)}>
      <S.PrimaryCTAButton>Manage users</S.PrimaryCTAButton>
    </Link>
  );
};

export const ManageUsageButton = ({organizationName}: ButtonProps) => {
  const url = accountSettingsUsagePage(organizationName);
  return (
    <S.SecondaryCTAButton
      onClick={() =>
        history.push(url, {
          from: {
            url: accountSettingsBillingTab(organizationName ?? ''),
            text: organizationName,
          },
        })
      }>
      Manage storage
    </S.SecondaryCTAButton>
  );
};

export const ManageStorageUsageButton = ({entityName}: EntityButtonProps) => {
  return (
    <Link
      to={storageFilePath(entityName, entityName, '', envIsPublicCloud, false)}>
      <S.SecondaryCTAButton>Manage usage</S.SecondaryCTAButton>
    </Link>
  );
};

export const ManageLocalDeploymentButton = () => {
  return (
    <S.SecondaryCTAButton>
      <TargetBlank href={deployer()}>Manage deployment</TargetBlank>
    </S.SecondaryCTAButton>
  );
};

export const ContactSalesButton = () => {
  return (
    <S.SecondaryCTAButton>
      <TargetBlank href={contactSalesPricing()}>Contact sales</TargetBlank>
    </S.SecondaryCTAButton>
  );
};

export const ErrorPlanCard = () => {
  return (
    <PlanCardWrapper>
      <ErrorBox />
    </PlanCardWrapper>
  );
};

const ACADEMIC_PLAN_DESCRIPTION = [
  'For academic projects only. Corporate use not allowed',
  'Unlimited experiments',
  'Unlimited tracked hours',
  'Storage is free up to 100GB, then usage is billed monthly',
];

// hardcoded plan cards specific to enterprise plan
export function getAcademicPlanCardProps(
  organizationName: string
): PlanCardProps {
  return {
    planName: 'Academic',
    subtitle: <S.SubtitleWrapper>Free</S.SubtitleWrapper>,
    description: (
      <S.DescriptionList>
        {ACADEMIC_PLAN_DESCRIPTION.map((text, i) => (
          <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
        ))}
      </S.DescriptionList>
    ),
    currentPlanCTA: <ManageUserButton organizationName={organizationName} />,
  };
}

const ENTERPRISE_PLAN_DESCRIPTION = [
  'Unlimited tracked hours* tracked',
  'Dedicated Machine Learning Engineer to ensure success',
  'Dedicated support channel',
  'Support SLA',
  'Custom storage plan',
  'Single sign on',
  'Service account for CI workflows',
];
const LOCAL_LICENSE_DESCRIPTION = [
  ...ENTERPRISE_PLAN_DESCRIPTION,
  'Run a W&B server locally on your own infrastructure',
];

export function getEnterprisePlanCardProps(
  organizationName: string
): PlanCardProps {
  return {
    planName: 'Enterprise',
    description: (
      <S.DescriptionList>
        {ENTERPRISE_PLAN_DESCRIPTION.map((text, i) => (
          <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
        ))}
      </S.DescriptionList>
    ),
    nextPlanCTA: (
      <S.PrimaryCTAButton
        onClick={() =>
          // This is an external URL, so we need it to be unprefixed
          // eslint-disable-next-line wandb/no-unprefixed-urls
          window.open(contactSalesPricing())
        }>
        Contact sales
      </S.PrimaryCTAButton>
    ),
    currentPlanCTA: <ManageUsageButton organizationName={organizationName} />,
  };
}

export function getEnterpriseTrialCardProps(
  organizationName: string,
  expiresAt?: Maybe<string>
): PlanCardProps {
  return {
    planName: 'Enterprise Trial',
    subtitle: expiresAt ? (
      <S.SubtitleWrapper>
        Trial expires on {new Date(expiresAt).toLocaleDateString()}
      </S.SubtitleWrapper>
    ) : undefined,
    description: (
      <S.DescriptionList>
        {ENTERPRISE_PLAN_DESCRIPTION.map((text, i) => (
          <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
        ))}
      </S.DescriptionList>
    ),
    nextPlanCTA: <ContactSalesButton />,
    currentPlanCTA: <ManageUserButton organizationName={organizationName} />,
  };
}

export const LOCAL_LICENSE_CARD: PlanCardProps = {
  planName: 'Local Deployment',
  description: (
    <S.DescriptionList>
      {LOCAL_LICENSE_DESCRIPTION.map((text, i) => (
        <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
      ))}
    </S.DescriptionList>
  ),
  currentPlanCTA: <ManageLocalDeploymentButton />,
};

export const PERSONAL_PLAN_CARD: PlanCardProps = {
  planName: 'Personal',
  subtitle: <S.SubtitleWrapper>Free</S.SubtitleWrapper>,
  description: (
    <S.DescriptionList>
      {[
        'For personal projects only. Corporate use not allowed',
        'Unlimited experiments',
        'Unlimited tracked hours',
        'Storage is free up to 100GB, then usage is billed monthly',
      ].map((text, i) => (
        <S.DescriptionListItem key={i}>{text}</S.DescriptionListItem>
      ))}
    </S.DescriptionList>
  ),
};
